import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import { Link } from 'react-router-dom';

interface TocItem {
  id: string;
  label: string;
}

interface ArticleLayoutProps {
  title: string;
  description: string;
  heroImage: string;
  heroImageAlt: string;
  datePublished: string;
  readTime: string;
  breadcrumbLabel: string;
  canonicalUrl: string;
  tocItems: TocItem[];
  faqItems: Array<{ q: string; a: string }>;
  children: React.ReactNode;
}

const ArticleLayout = ({
  title,
  description,
  heroImage,
  heroImageAlt,
  datePublished,
  readTime,
  breadcrumbLabel,
  canonicalUrl,
  tocItems,
  faqItems,
  children,
}: ArticleLayoutProps) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kakobuyfinds.boats';

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map((item) => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a,
      },
    })),
  };

  return (
    <>
      <Navbar />
      <main>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": title,
            "description": description,
            "author": {
              "@type": "Organization",
              "name": "Kakobuy Spreadsheet Hub",
            },
            "datePublished": datePublished,
            "dateModified": datePublished,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": canonicalUrl,
            },
            "publisher": {
              "@type": "Organization",
              "name": "Kakobuy Spreadsheet Hub",
              "url": siteUrl,
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>

        {/* Article Hero */}
        <section className="relative w-full py-24 md:py-32 px-4 md:px-6 lg:px-10 bg-gradient-to-br from-background-100 via-background-50 to-accent-50/40">
          <div className="absolute top-16 left-10 w-56 h-56 rounded-full bg-primary-100/30 blur-3xl"></div>
          <div className="absolute bottom-10 right-16 w-72 h-72 rounded-full bg-accent-100/20 blur-3xl"></div>

          <div className="relative max-w-3xl mx-auto text-center">
            <nav className="flex items-center justify-center gap-2 text-xs text-foreground-500 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors duration-200">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-400">{breadcrumbLabel}</span>
            </nav>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 leading-[1.12] mb-5">
              {title}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 max-w-xl mx-auto mb-6 leading-relaxed">
              {description}
            </p>
            <div className="flex items-center justify-center gap-4 text-xs text-foreground-500">
              <div className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                <span>Updated {datePublished}</span>
              </div>
              <span className="text-foreground-300">·</span>
              <div className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                <span>{readTime}</span>
              </div>
              <span className="text-foreground-300">·</span>
              <div className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                <span>By Kakobuy Hub Team</span>
              </div>
            </div>
          </div>

          <div className="relative max-w-4xl mx-auto mt-10">
            <div className="w-full h-52 md:h-64 rounded-2xl overflow-hidden">
              <img
                src={heroImage}
                alt={heroImageAlt}
                className="w-full h-full object-cover object-top"
                loading="eager"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="w-full py-12 md:py-16 px-4 md:px-6 lg:px-10">
          <div className="max-w-[720px] mx-auto">
            <div className="prose-custom text-foreground-800 leading-relaxed">

              {/* Table of Contents */}
              <div className="bg-background-100 rounded-2xl p-6 mb-10 border border-background-200/60">
                <h2 className="font-heading font-bold text-foreground-950 text-lg mb-4">
                  Table of Contents
                </h2>
                <ol className="space-y-2 text-sm text-foreground-700 list-decimal list-inside">
                  {tocItems.map((item, i) => (
                    <li key={item.id}>
                      <a href={`#${item.id}`} className="text-primary-500 hover:text-primary-600">{item.label}</a>
                    </li>
                  ))}
                </ol>
              </div>

              {children}

            </div>

            {/* Article Footer CTA */}
            <div className="mt-12 p-8 bg-background-100 rounded-2xl border border-background-200/60 text-center">
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">
                Ready to Explore More?
              </h3>
              <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
                Browse the complete Kakobuy Spreadsheet database or read more in-depth guides.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/"
                  className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Back to Homepage
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 rounded-full border-2 border-primary-500 text-primary-600 text-sm font-semibold hover:bg-primary-500 hover:text-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Visit Main Website
                  <i className="ri-arrow-right-up-line ml-1"></i>
                </a>
              </div>
            </div>

            {/* Related Internal Links */}
            <div className="mt-8 p-6 bg-background-50 rounded-2xl border border-background-200/60">
              <h4 className="font-heading font-semibold text-foreground-900 text-sm uppercase tracking-wider mb-4">
                Related Articles
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link to="/kakobuy-spreadsheet-ultimate-guide-2026" className="flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200">
                  <i className="ri-article-line"></i>
                  <span>Ultimate Guide 2026</span>
                </Link>
                <Link to="/kakobuy-vs-cssbuy-vs-superbuy-comparison" className="flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200">
                  <i className="ri-article-line"></i>
                  <span>Platform Comparison</span>
                </Link>
                <Link to="/kakobuy-spreadsheet-safety-buying-guide" className="flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200">
                  <i className="ri-article-line"></i>
                  <span>Safety & Buying Guide</span>
                </Link>
                <Link to="/kakobuy-spreadsheet-beginner-guide" className="flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200">
                  <i className="ri-article-line"></i>
                  <span>Beginner Guide</span>
                </Link>
                <Link to="/kakobuy-shoes-spreadsheet-guide" className="flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200">
                  <i className="ri-article-line"></i>
                  <span>Shoes Spreadsheet Guide</span>
                </Link>
                <Link to="/kakobuy-spreadsheet-tips-and-tricks" className="flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200">
                  <i className="ri-article-line"></i>
                  <span>Tips & Tricks</span>
                </Link>
              </div>
            </div>

          </div>
        </article>
      </main>
      <Footer />
    </>
  );
};

export default ArticleLayout;